import { useEffect, useMemo, useState } from "react";
import {
  DollarSign,
  Home,
  MessageSquare,
  Shield,
  TriangleAlert,
  TrendingDown,
  TrendingUp,
  Users,
  UsersRound,
  UserCheck,
  AlertCircle,
  Info,
  Car,
  QrCode,
  Building2,
  Zap,
  ArrowUpRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import Button from "../../../components/ui/Button";
import Dropdown from "../../../components/ui/Dropdown";
import { getDashboardStats } from "../../../services/dashboardService";
import { getStoredAuthUser, getCurrentRoleKey } from "../../../services/authService";
import { getResidentVisitorRequests } from "../../../services/visitorService";
import { getMyPreApprovedInvites, adminGateEntryLogs, getExpectedInvites } from "../../../services/inviteService";
import { getGuardVisitorEntries } from "../../../services/visitorService";
import { getSocieties, getTowersBySociety } from "../../../services/societyService";
import { getFlatsBySociety } from "../../../services/flatsService";
import { createVisitorEntry, getVisitorTypes, resolveResidentForUnit } from "../../../services/visitorService";
import { toast } from "react-toastify";
import { getAuditLogs } from "../../../services/systemLogsService";
import { getAdminMasterBbpsCategories } from "../../../services/bbpsAdminService";
import Spinner from "../../../components/ui/Spinner";
import ReactChart from "../../../components/charts/ReactChart";
import { visitorEntrySchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import styles from "./Dashboard.module.css";

const RESIDENT_ROLE_KEYS = ["user", "owner", "flat-owner", "tenant", "family-member", "resident"];
const GUARD_ROLE_KEYS = ["guard", "security-head"];

const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-IN").format(value);
};

const formatMoneyLakhs = (value) => {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  return `₹${new Intl.NumberFormat("en-IN").format(num)}`;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [bbpsData, setBbpsData] = useState(null);
  const [residentStats, setResidentStats] = useState(null);
  const [guardStats, setGuardStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [gateLogs, setGateLogs] = useState([]);
  const [auditLogsError, setAuditLogsError] = useState("");
  const [gateLogsError, setGateLogsError] = useState("");
  const [guardFormLoading, setGuardFormLoading] = useState(false);
  const [guardFormSubmitting, setGuardFormSubmitting] = useState(false);
  const [guardTowers, setGuardTowers] = useState([]);
  const [guardFlats, setGuardFlats] = useState([]);
  const [guardVisitorTypes, setGuardVisitorTypes] = useState([]);
  const [guardTowerId, setGuardTowerId] = useState("");
  const [guardUnitId, setGuardUnitId] = useState("");
  const [guardResolvedResident, setGuardResolvedResident] = useState(null);
  const [expectedInvites, setExpectedInvites] = useState([]);
  const [guardForm, setGuardForm] = useState({
    visitor_name: "",
    visitor_phone: "",
    visitor_type_id: "",
    purpose: "",
    vehicle_number: "",
    no_of_visitors: 1,
    remarks: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const currentRoleKey = getCurrentRoleKey();
  const isResidentDashboard = RESIDENT_ROLE_KEYS.includes(currentRoleKey);
  const isGuardDashboard = GUARD_ROLE_KEYS.includes(currentRoleKey);
  const isSimpleDashboard = isResidentDashboard || isGuardDashboard;
  const authUser = getStoredAuthUser();
  const canPickSociety = currentRoleKey === "super-admin" || currentRoleKey === "admin";
  const [societies, setSocieties] = useState([]);
  const [selectedSocietyId, setSelectedSocietyId] = useState("");

  const effectiveSocietyId = canPickSociety
    ? selectedSocietyId
    : authUser?.society_id
    ? String(authUser.society_id)
    : "";

  useEffect(() => {
    if (isSimpleDashboard) return;

    if (!canPickSociety) {
      if (authUser?.society_id != null) setSelectedSocietyId(String(authUser.society_id));
      return;
    }

    let mounted = true;
    const load = async () => {
      try {
        const data = await getSocieties();
        if (!mounted) return;
        setSocieties(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setSocieties([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [authUser?.society_id, canPickSociety, isSimpleDashboard]);

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      try {
        if (isResidentDashboard) {
          const residentUserId = Number(authUser?.user_id);
          const [requests, invitesRes] = await Promise.all([
            residentUserId ? getResidentVisitorRequests({ residentUserId }) : Promise.resolve([]),
            getMyPreApprovedInvites(),
          ]);

          const requestRows = Array.isArray(requests) ? requests : [];
          const pendingRequests = requestRows.filter((r) => r?.status === "PENDING");

          const invites = invitesRes?.invites || [];
          const activeInvites = (Array.isArray(invites) ? invites : []).filter(
            (i) => String(i?.status || "").toLowerCase() === "active"
          );

          if (isMounted) {
            setResidentStats({
              pendingApprovals: pendingRequests.length,
              activeInvites: activeInvites.length,
              totalRequests: requestRows.length,
            });
            setError("");
          }
        } else if (isGuardDashboard) {
          const societyId = Number(authUser?.society_id);
          const entries = societyId ? await getGuardVisitorEntries({ societyId }) : [];
          const rows = Array.isArray(entries) ? entries : [];

          const pending = rows.filter((r) => r?.status === "PENDING").length;
          const approved = rows.filter((r) => r?.status === "APPROVED").length;
          const checkedIn = rows.filter((r) => r?.status === "CHECKED_IN").length;
          const checkedOut = rows.filter((r) => r?.status === "CHECKED_OUT").length;

          if (isMounted) {
            setGuardStats({
              pendingRequests: pending,
              approvedRequests: approved,
              checkedIn,
              checkedOut,
            });
            setError("");
          }
        } else {
          const [dataRes, auditRes, gateRes, bbpsRes] = await Promise.allSettled([
            getDashboardStats({ societyId: effectiveSocietyId || undefined }),
            getAuditLogs({ limit: 10 }),
            adminGateEntryLogs({ limit: 10, society_id: effectiveSocietyId || undefined }),
            getAdminMasterBbpsCategories(),
          ]);
          if (isMounted) {
            setStats(dataRes.status === "fulfilled" ? dataRes.value : null);
            setAuditLogs(auditRes.status === "fulfilled" ? auditRes.value?.logs || [] : []);
            setGateLogs(gateRes.status === "fulfilled" ? gateRes.value?.logs || [] : []);
            setBbpsData(bbpsRes.status === "fulfilled" ? bbpsRes.value : null);
            setAuditLogsError(auditRes.status === "rejected" ? "Unable to load activity logs" : "");
            setGateLogsError(gateRes.status === "rejected" ? "Unable to load security logs" : "");
            setError("");
          }
        }
      } catch (loadError) {
        console.error(loadError);
        if (isMounted) setError("Unable to load dashboard data right now.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadStats();
    return () => {
      isMounted = false;
    };
  }, [authUser?.society_id, authUser?.user_id, effectiveSocietyId, isGuardDashboard, isResidentDashboard]);

  useEffect(() => {
    if (!isGuardDashboard) return;
    const societyId = Number(authUser?.society_id);
    if (!societyId) return;

    let mounted = true;
    
    const loadGuardFormData = async () => {
      setGuardFormLoading(true);
      try {
        const [towers, flats, types, invitesData] = await Promise.all([
          getTowersBySociety(societyId),
          getFlatsBySociety(societyId),
          getVisitorTypes(),
          getExpectedInvites(),
        ]);
        if (!mounted) return;
        setGuardTowers(towers || []);
        setGuardFlats(flats || []);
        setGuardVisitorTypes(types || []);
        setExpectedInvites(Array.isArray(invitesData) ? invitesData : (invitesData?.invites || []));
        if ((towers || []).length) {
          setGuardTowerId(String(towers[0].tower_id));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (mounted) setGuardFormLoading(false);
      }
    };

    loadGuardFormData();
    return () => {
      mounted = false;
    };
  }, [authUser?.society_id, isGuardDashboard]);

  useEffect(() => {
    if (!isGuardDashboard) return;
    setGuardUnitId("");
    setGuardResolvedResident(null);
  }, [guardTowerId, isGuardDashboard]);

  useEffect(() => {
    if (!isGuardDashboard) return;
    const run = async () => {
      const unitId = Number(guardUnitId);
      if (!unitId) {
        setGuardResolvedResident(null);
        return;
      }
      try {
        const data = await resolveResidentForUnit(unitId);
        setGuardResolvedResident(data);
      } catch (e) {
        console.error(e);
        setGuardResolvedResident(null);
      }
    };
    run();
  }, [guardUnitId, isGuardDashboard]);

  const guardFlatsForTower = useMemo(() => {
    const nextTowerId = Number(guardTowerId);
    if (!nextTowerId) return [];
    const towerFlats = (guardFlats || []).filter((f) => Number(f.tower_id) === nextTowerId);

    const mergedFlatNumbers = new Set(
      towerFlats
        .map((f) => String(f.flat_number || ""))
        .filter((n) => n.includes("+"))
    );

    return towerFlats.filter((f) => {
      const flatNumber = String(f.flat_number || "");
      const unitType = String(f.unit_type || "");

      if (unitType.toUpperCase() === "MERGED") return false;
      if (flatNumber.startsWith("M-")) return false;
      if (flatNumber.includes("+")) return true;
      if (mergedFlatNumbers.has(String(f.merged_from || ""))) return false;
      return true;
    });
  }, [guardFlats, guardTowerId]);

  const kpis = useMemo(() => {
    if (isResidentDashboard) {
      return [
        {
          label: "Pending Approvals",
          value: formatNumber(residentStats?.pendingApprovals ?? 0),
          delta: "Tap to review",
          direction: "up",
          icon: Shield,
          iconClass: styles.iconOrange,
          onClick: () => navigate("/resident/visitors"),
        },
        {
          label: "Active Invites",
          value: formatNumber(residentStats?.activeInvites ?? 0),
          delta: "Gate passes",
          direction: "neutral",
          icon: Users,
          iconClass: styles.iconPurple,
          onClick: () => navigate("/resident/invites"),
        },
        {
          label: "Visitor Requests",
          value: formatNumber(residentStats?.totalRequests ?? 0),
          delta: "All time",
          direction: "neutral",
          icon: UsersRound,
          iconClass: styles.iconBlue,
          onClick: () => navigate("/resident/visitors"),
        },
        {
          label: "My Profile",
          value: authUser?.full_name || authUser?.email_id || "View",
          delta: "Account",
          direction: "neutral",
          icon: Home,
          iconClass: styles.iconGreen,
          onClick: () => navigate("/profile"),
        },
      ];
    }

    if (isGuardDashboard) {
      return [
        {
          label: "Pending Requests",
          value: formatNumber(guardStats?.pendingRequests ?? 0),
          delta: "Need approval",
          direction: "alert",
          icon: Shield,
          iconClass: styles.iconOrange,
          onClick: () => navigate("/guard/visitors"),
        },
        {
          label: "Approved (Waiting)",
          value: formatNumber(guardStats?.approvedRequests ?? 0),
          delta: "Ready to check-in",
          direction: "neutral",
          icon: Users,
          iconClass: styles.iconBlue,
          onClick: () => navigate("/guard/visitors"),
        },
        {
          label: "Checked In",
          value: formatNumber(guardStats?.checkedIn ?? 0),
          delta: "Inside",
          direction: "neutral",
          icon: UsersRound,
          iconClass: styles.iconPurple,
          onClick: () => navigate("/guard/visitors"),
        },
        {
          label: "Expected Visitors",
          value: formatNumber(expectedInvites?.length ?? 0),
          delta: "Pre-approved",
          direction: "neutral",
          icon: Users,
          iconClass: styles.iconGreen,
          onClick: () => navigate("/guard/expected-visitors"),
        },
      ];
    }

    if (stats?.property_type === "INDUSTRIAL") {
      const ind = stats.industrial || {};
      return [
        {
          label: "Pending Host Approvals",
          value: formatNumber(ind.pending_approvals ?? 0),
          delta: ind.pending_approvals > 0 ? "Action Required" : "All Clear",
          direction: ind.pending_approvals > 0 ? "alert" : "neutral",
          icon: UserCheck,
          iconClass: styles.iconOrange,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
        {
          label: "Escalated to Reception",
          value: formatNumber(ind.escalated_reception ?? 0),
          delta: ind.escalated_reception > 0 ? "High Priority" : "None",
          direction: ind.escalated_reception > 0 ? "alert" : "neutral",
          icon: Shield,
          iconClass: styles.iconPink,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
        {
          label: "Visitors Inside",
          value: formatNumber(ind.active_visits ?? 0),
          delta: "Checked In",
          direction: "neutral",
          icon: UsersRound,
          iconClass: styles.iconBlue,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
        {
          label: "Vehicles Inside",
          value: formatNumber(ind.vehicles_inside ?? 0),
          delta: "Parked / Loading",
          direction: "neutral",
          icon: Car,
          iconClass: styles.iconPurple,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
        {
          label: "Active Gate Drafts",
          value: formatNumber(ind.active_drafts ?? 0),
          delta: "Awaiting Form",
          direction: "neutral",
          icon: QrCode,
          iconClass: styles.iconGreen,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
        {
          label: "Guards On Duty",
          value: formatNumber(ind.on_duty_guards ?? 0),
          delta: "Active Posts",
          direction: "up",
          icon: Shield,
          iconClass: styles.iconBlue,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=staff`),
        },
        {
          label: "Departments",
          value: formatNumber(ind.departments_count ?? 0),
          delta: "Configured",
          direction: "neutral",
          icon: Building2,
          iconClass: styles.iconPurple,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=towers`),
        },
        {
          label: "Overdue Auto-Checked Out",
          value: formatNumber(ind.auto_checked_out ?? 0),
          delta: "Past Midnight",
          direction: ind.auto_checked_out > 0 ? "down" : "neutral",
          icon: AlertCircle,
          iconClass: styles.iconOrange,
          onClick: () => navigate(`/properties?propertyId=${effectiveSocietyId}&tab=visits`),
        },
      ];
    }

    const totalUsers = stats?.totals?.users ?? null;
    const activeVisitors = stats?.totals?.active_visitors ?? null;
    const pendingTickets = stats?.totals?.pending_support_tickets ?? null;
    const occupiedUnits = stats?.totals?.occupied_units ?? null;
    const totalUnits = stats?.totals?.total_units ?? null;

    return [
        {
          label: "Pending Approvals",
          value: formatNumber(stats?.totals?.pending_approvals ?? null),
          delta: "Action Required",
          direction: "alert",
          icon: UserCheck,
          iconClass: styles.iconOrange,
          onClick: () => navigate("/users?tab=pending"),
        },
        {
          label: "Active Visitors",
          value: formatNumber(activeVisitors),
          delta: "+8.2%",
          direction: "up",
          icon: Users,
          iconClass: styles.iconPink,
          onClick: () => navigate("/admin/gate-entry-logs"),
        },
        {
          label: "Total Residents",
          value: formatNumber(totalUsers),
          delta: "+12.5%",
          direction: "up",
          icon: UsersRound,
          iconClass: styles.iconBlue,
          onClick: () => navigate("/users"),
        },
      {
        label: "Pending Complaints",
        value: formatNumber(pendingTickets),
        delta: "-15.3%",
        direction: "down",
        icon: MessageSquare,
        iconClass: styles.iconOrange,
        onClick: () => navigate("/support"),
      },
      {
        label: "Monthly Collection",
        value: formatMoneyLakhs(stats?.totals?.collections ?? null),
        delta: "+23.1%",
        direction: "up",
        icon: DollarSign,
        iconClass: styles.iconGreen,
        onClick: () => toast.info("Collections module coming soon."),
      },
      {
        label: "Maintenance Due",
        value: "₹2.8L",
        delta: "-5.2%",
        direction: "down",
        icon: TriangleAlert,
        iconClass: styles.iconOrange,
        onClick: () => navigate("/support"),
      },
      {
        label: "Security Alerts",
        value: "3",
        delta: "-60%",
        direction: "down",
        icon: Shield,
        iconClass: styles.iconPink,
        onClick: () => navigate("/admin/gate-entry-logs"),
      },
      {
        label: "Occupied Units",
        value:
          occupiedUnits !== null && totalUnits !== null
            ? `${formatNumber(occupiedUnits)}/${formatNumber(totalUnits)}`
            : "—",
        delta: "+1.5%",
        direction: "up",
        icon: Home,
        iconClass: styles.iconPurple,
        onClick: () => {
          if (currentRoleKey === "super-admin") return navigate("/properties");
          if (currentRoleKey === "society-admin" || currentRoleKey === "society-manager") {
            return navigate("/floor-summary");
          }
          return navigate("/properties");
        },
      },
    ];
  }, [
    authUser?.email_id,
    authUser?.full_name,
    guardStats,
    isGuardDashboard,
    isResidentDashboard,
    navigate,
    residentStats,
    currentRoleKey,
    stats,
    expectedInvites,
    effectiveSocietyId,
  ]);

  const auditActivityRows = useMemo(() => {
    const pickTag = (module) => {
      const m = String(module || "").toUpperCase();
      if (m === "VISITOR") return { label: "visitor", className: styles.tagBlue };
      if (m === "GATE") return { label: "gate", className: styles.tagPurple };
      if (m === "INVITE") return { label: "invite", className: styles.tagGreen };
      if (m === "USER") return { label: "user", className: styles.tagOrange };
      return { label: "log", className: styles.tagBlue };
    };

    return (auditLogs || []).map((l) => ({
      key: String(l.id),
      name: l.user_name || l.system_role || l.primary_role || "Activity",
      text: l.description || l.action || "-",
      time: l.created_at ? new Date(l.created_at).toLocaleString() : "-",
      tag: pickTag(l.module),
    }));
  }, [auditLogs]);

  const securityLogRows = useMemo(() => {
    return (gateLogs || []).map((l) => ({
      key: String(l.id),
      name: l.visitor_name || "Visitor",
      text: `${l.entry_type || "ENTRY"} • ${l.entry_status || "-"}`,
      time: l.created_at ? new Date(l.created_at).toLocaleString() : "-",
      tag: { label: "security", className: styles.tagPurple },
      meta: l.mobile_number || l.vehicle_number || "",
    }));
  }, [gateLogs]);

  return (
    <AdminLayout title="">
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.heroShapesContainer}>
            <div className={styles.heroShape1} aria-hidden="true" />
            <div className={styles.heroShape2} aria-hidden="true" />
            <div className={styles.heroShape3} aria-hidden="true" />
          </div>
          <h2 className={styles.heroTitle}>
            {isResidentDashboard ? "Home" : isGuardDashboard ? "Guard Home" : "Dashboard Overview"}
          </h2>
          <p className={styles.heroSub}>
            {isResidentDashboard
              ? "Quick updates and actions for your home."
              : isGuardDashboard
              ? "Quick actions and counts for security operations."
              : "Welcome back! Here's what's happening in your society today."}
          </p>

          {!isSimpleDashboard ? (
            <div className={styles.heroActions}>
              <div className={styles.heroFilter}>
                <div className={styles.heroFilterLabel}>Society</div>
                <Dropdown
                  value={effectiveSocietyId}
                  onChange={(value) => setSelectedSocietyId(value)}
                  options={[
                    ...(canPickSociety ? [{ label: "All Societies", value: "" }] : []),
                    ...(societies || []).map((s) => ({
                      label: s.society_name,
                      value: String(s.society_id),
                    })),
                  ]}
                  disabled={!canPickSociety}
                />
              </div>
            </div>
          ) : null}

          {isResidentDashboard ? (
            <div className={styles.heroActions}>
              <Button className={styles.smallButton} onClick={() => navigate("/resident/visitors")}>
                Visitor Requests
              </Button>
              <Button
                variant="outline"
                className={styles.smallButton}
                onClick={() => navigate("/resident/invites")}
              >
                My Invites
              </Button>
              <Button
                variant="outline"
                className={styles.smallButton}
                onClick={() => navigate("/resident/invites/new")}
              >
                Create Invite
              </Button>
              <Button
                variant="outline"
                className={styles.smallButton}
                onClick={() => navigate("/resident/amenities")}
              >
                Book Amenities
              </Button>
            </div>
          ) : isGuardDashboard ? (
            <div className={styles.heroActions}>
              <Button className={styles.smallButton} onClick={() => navigate("/guard/visitors")}>
                Visitor Entries
              </Button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className={styles.statsGrid}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={`${styles.kpiCard} ${styles.skeletonCard}`}>
                <div>
                  <div className={styles.skeletonLabel}></div>
                  <div className={styles.skeletonValue}></div>
                  <div className={styles.skeletonTrend}></div>
                </div>
                <div className={styles.skeletonIcon}></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className={styles.errorCard}>{error}</div>
        ) : (
          <div className={styles.statsGrid}>
            {kpis.map((kpi) => {
              const Icon = kpi.icon;
              let TrendIcon = null;
              let deltaClass = styles.kpiNeutral;

              if (kpi.direction === "up") {
                TrendIcon = TrendingUp;
                deltaClass = styles.kpiUp;
              } else if (kpi.direction === "down") {
                TrendIcon = TrendingDown;
                deltaClass = styles.kpiDown;
              } else if (kpi.direction === "alert") {
                TrendIcon = AlertCircle;
                deltaClass = styles.kpiAlert;
              } else if (kpi.direction === "neutral") {
                TrendIcon = Info;
                deltaClass = styles.kpiNeutral;
              }

              const isMetric = kpi.direction === "up" || kpi.direction === "down";

              return (
                <div
                  key={kpi.label}
                  className={[
                    styles.kpiCard,
                    kpi.direction === "alert" ? styles.kpiCardAlert : "",
                    kpi.onClick ? styles.kpiClickable : ""
                  ].filter(Boolean).join(" ")}
                  role={kpi.onClick ? "button" : undefined}
                  tabIndex={kpi.onClick ? 0 : undefined}
                  onClick={kpi.onClick}
                  onKeyDown={(e) => {
                    if (!kpi.onClick) return;
                    if (e.key === "Enter" || e.key === " ") kpi.onClick();
                  }}
                >
                  <div>
                    <div className={styles.kpiLabel}>{kpi.label}</div>
                    <div className={styles.kpiValue}>{kpi.value}</div>
                    <div className={styles.kpiTrend}>
                      {TrendIcon && <TrendIcon size={16} className={deltaClass} />}
                      <span className={deltaClass}>{kpi.delta}</span>
                      {!isSimpleDashboard && isMetric && (
                        <span>vs last month</span>
                      )}
                    </div>
                  </div>
                  <div className={[styles.kpiIcon, kpi.iconClass].join(" ")}>
                    <Icon size={22} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {isGuardDashboard ? (
          <section className={styles.formCard}>
            <div className={styles.formTitleRow}>
              <div>
                <h3 className={styles.formTitle}>Request Approval</h3>
                <p className={styles.formSub}>
                  Send an approval request to the resident for a guest entry.
                </p>
              </div>
              <Button variant="outline" onClick={() => navigate("/guard/visitors")}>
                Open full page
              </Button>
            </div>

            {guardFormLoading ? (
              <div style={{ padding: "18px 0" }}>
                <Spinner />
              </div>
            ) : (
              <form
                className={styles.formGrid}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const parsed = visitorEntrySchema.safeParse({
                    ...guardForm,
                    society_id: authUser?.society_id,
                    tower_id: guardTowerId,
                    unit_id: guardUnitId,
                    resident_user_id: guardResolvedResident?.resident_user_id || null,
                    user_id: Number(authUser?.user_id) || undefined,
                  });
                  if (!parsed.success) return toast.error(zodErrorToFirstMessage(parsed.error));

                  setGuardFormSubmitting(true);
                  try {
                    await createVisitorEntry(parsed.data);
                    toast.success("Approval request sent");
                    setGuardForm({
                      visitor_name: "",
                      visitor_phone: "",
                      visitor_type_id: "",
                      purpose: "",
                      vehicle_number: "",
                      no_of_visitors: 1,
                      remarks: "",
                    });
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.data?.error || err?.data?.message || err?.message || "Unable to send request");
                  } finally {
                    setGuardFormSubmitting(false);
                  }
                }}
              >
                <div className={styles.field}>
                  <div className={styles.label}>Tower</div>
                  <Dropdown
                    value={guardTowerId}
                    onChange={(val) => setGuardTowerId(val)}
                    options={(guardTowers || []).map((t) => ({
                      label: t.tower_name,
                      value: String(t.tower_id),
                    }))}
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Unit</div>
                  <Dropdown
                    value={guardUnitId}
                    onChange={(val) => setGuardUnitId(val)}
                    options={[
                      { label: "Select unit", value: "" },
                      ...guardFlatsForTower.map((f) => ({
                        label: String(f.flat_number),
                        value: String(f.flat_id),
                      })),
                    ]}
                  />
                  <div className={styles.pill}>
                    Resident: {guardResolvedResident?.resident_name || (guardUnitId ? "Not assigned" : "-")}
                  </div>
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Visitor Name</div>
                  <input
                    className={styles.input}
                    value={guardForm.visitor_name}
                    onChange={(e) => setGuardForm((p) => ({ ...p, visitor_name: e.target.value }))}
                    placeholder="Enter name"
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Mobile Number</div>
                  <input
                    className={styles.input}
                    value={guardForm.visitor_phone}
                    onChange={(e) =>
                      setGuardForm((p) => ({
                        ...p,
                        visitor_phone: String(e.target.value || "").replace(/\D/g, "").slice(0, 10),
                      }))
                    }
                    placeholder="10-digit mobile"
                    inputMode="numeric"
                    maxLength={10}
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Visitor Type</div>
                  <Dropdown
                    value={guardForm.visitor_type_id}
                    onChange={(val) => setGuardForm((p) => ({ ...p, visitor_type_id: val }))}
                    options={[
                      { label: "Select type", value: "" },
                      ...(guardVisitorTypes || []).map((t) => ({
                        label: String(t.name),
                        value: String(t.id),
                      })),
                    ]}
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Purpose</div>
                  <input
                    className={styles.input}
                    value={guardForm.purpose}
                    onChange={(e) => setGuardForm((p) => ({ ...p, purpose: e.target.value }))}
                    placeholder="Purpose"
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>Vehicle Number</div>
                  <input
                    className={styles.input}
                    value={guardForm.vehicle_number}
                    onChange={(e) => setGuardForm((p) => ({ ...p, vehicle_number: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>

                <div className={styles.field}>
                  <div className={styles.label}>No. of Visitors</div>
                  <input
                    className={styles.input}
                    type="number"
                    min={1}
                    value={guardForm.no_of_visitors}
                    onChange={(e) => {
                      const digits = String(e.target.value || "").replace(/\D/g, "");
                      const num = Math.max(1, Math.min(99, Number(digits || 1)));
                      setGuardForm((p) => ({ ...p, no_of_visitors: num }));
                    }}
                  />
                </div>

                <div className={[styles.field, styles.fieldFull].join(" ")}>
                  <div className={styles.label}>Remarks</div>
                  <input
                    className={styles.input}
                    value={guardForm.remarks}
                    onChange={(e) => setGuardForm((p) => ({ ...p, remarks: e.target.value }))}
                    placeholder="Optional"
                  />
                </div>

                <div className={styles.fieldFull}>
                  <Button disabled={guardFormSubmitting} fullWidth type="submit">
                    {guardFormSubmitting ? "Submitting..." : "Send Approval Request"}
                  </Button>
                </div>
              </form>
            )}
          </section>
        ) : null}

        {isSimpleDashboard ? null : (
          <>
            {/* BBPS White-Label BillPay Integration Card */}
            <section className={styles.bbpsWidgetCard}>
              <div className={styles.bbpsWidgetHeader}>
                <div className={styles.bbpsWidgetTitleRow}>
                  <div className={styles.bbpsBadge}>
                    <Zap size={22} />
                  </div>
                  <div>
                    <h3 className={styles.panelTitle}>Bharat BillPay (BBPS) Integration</h3>
                    <p className={styles.panelSub}>
                      Setu White-Label Ethereal engine — Live in-app bill presentation &amp; UPI collections
                    </p>
                  </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className={styles.bbpsLiveIndicator}>
                    <span className={styles.bbpsLiveDot} />
                    <span>Setu Production Active</span>
                  </div>
                  <Button
                    variant="outline"
                    className={styles.smallButton}
                    onClick={() => navigate("/settings?menu=bbps-categories")}
                  >
                    Manage Categories <ArrowUpRight size={14} style={{ marginLeft: 4 }} />
                  </Button>
                </div>
              </div>

              <div className={styles.bbpsCategoriesGrid}>
                {(bbpsData?.categories || [
                  { id: "electricity", title: "Electricity", tag: "Utility", color: "#eab308" },
                  { id: "water", title: "Water", tag: "Utility", color: "#0284c7" },
                  { id: "piped_gas", title: "Piped Gas", tag: "Utility", color: "#ea580c" },
                  { id: "broadband", title: "Broadband", tag: "Telecom", color: "#4f46e5" },
                  { id: "mobile_postpaid", title: "Mobile Postpaid", tag: "Telecom", color: "#059669" },
                  { id: "mobile_prepaid", title: "Mobile Prepaid", tag: "Telecom", color: "#10b981" },
                  { id: "fastag", title: "FASTag", tag: "Transport", color: "#d97706" },
                  { id: "dth", title: "DTH", tag: "Media", color: "#dc2626" },
                ]).slice(0, 8).map((cat) => {
                  const isEnabled = bbpsData?.globalSettings ? Boolean(bbpsData.globalSettings[cat.id]) : true;
                  return (
                    <div
                      key={cat.id}
                      className={[
                        styles.bbpsCategoryPill,
                        isEnabled ? styles.bbpsCategoryActive : styles.bbpsCategoryDisabled
                      ].join(" ")}
                    >
                      <div className={styles.bbpsCategoryLeft}>
                        <div
                          className={styles.bbpsCategoryIconWrap}
                          style={{
                            background: String(cat.color || "#2563eb") + "15",
                            color: cat.color || "#2563eb"
                          }}
                        >
                          <Zap size={14} />
                        </div>
                        <span className={styles.bbpsCategoryName}>{cat.title}</span>
                      </div>
                      <span className={[styles.bbpsCategoryTag, isEnabled ? styles.bbpsTagActive : styles.bbpsTagOff].join(" ")}>
                        {isEnabled ? "Live" : "Off"}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className={styles.bbpsWidgetFooter}>
                <span>
                  NPCI Bharat BillPay COU Gateway: <strong>coudc.setu.co</strong> &bull; Partner ID: <strong>207569...831</strong>
                </span>
                <span>
                  Resident Mobile App: <strong>White-Label Seamless In-App Sessions</strong>
                </span>
              </div>
            </section>

            <div className={styles.panelsGrid}>
              <section className={styles.panelCard}>
                <h3 className={styles.panelTitle}>Visitor Analytics</h3>
                <p className={styles.panelSub}>Daily visitor count for the current week</p>
                <ReactChart
                  type="line"
                  height={260}
                  xKey="day"
                  data={[
                    { day: "Mon", visitors: 45 },
                    { day: "Tue", visitors: 52 },
                    { day: "Wed", visitors: 38 },
                    { day: "Thu", visitors: 66 },
                    { day: "Fri", visitors: 54 },
                    { day: "Sat", visitors: 74 },
                    { day: "Sun", visitors: 58 },
                  ]}
                  series={[{ key: "visitors", color: "#4f46e5", name: "visitors" }]}
                />
              </section>

              <section className={styles.panelCard}>
                <h3 className={styles.panelTitle}>Revenue Overview</h3>
                <p className={styles.panelSub}>Monthly revenue breakdown (in Lakhs)</p>
                <ReactChart
                  type="bar"
                  height={260}
                  xKey="month"
                  data={[
                    { month: "Jan", maintenance: 11.0, parking: 3.2, amenities: 2.1 },
                    { month: "Feb", maintenance: 10.5, parking: 3.4, amenities: 2.2 },
                    { month: "Mar", maintenance: 12.0, parking: 3.1, amenities: 2.3 },
                    { month: "Apr", maintenance: 11.2, parking: 3.6, amenities: 2.4 },
                    { month: "May", maintenance: 12.4, parking: 3.8, amenities: 2.5 },
                    { month: "Jun", maintenance: 11.8, parking: 3.5, amenities: 2.6 },
                  ]}
                  series={[
                    { key: "maintenance", color: "#4f46e5", name: "maintenance" },
                    { key: "parking", color: "#a855f7", name: "parking" },
                    { key: "amenities", color: "#06b6d4", name: "amenities" },
                  ]}
                />
              </section>
            </div>

            <div className={styles.lowerGrid}>
              <section className={styles.panelCard}>
                <h3 className={styles.panelTitle}>Security Logs</h3>
                <p className={styles.panelSub}>Latest gate entry log events</p>

                <div className={styles.activityList}>
                  {gateLogsError ? (
                    <div className={styles.activityTime}>{gateLogsError}</div>
                  ) : securityLogRows.length === 0 ? (
                    <div className={styles.activityTime}>No security logs yet.</div>
                  ) : (
                    securityLogRows.slice(0, 5).map((a) => (
                      <div key={a.key} className={styles.activityRow}>
                        <div className={styles.avatarStub} aria-hidden="true" />
                        <div className={styles.activityBody}>
                          <div className={styles.activityName}>
                            {a.name} {a.meta ? <span className={styles.activityTime}>• {a.meta}</span> : null}
                          </div>
                          <div className={styles.activityText}>{a.text}</div>
                          <div className={styles.activityTime}>{a.time}</div>
                        </div>
                        <div className={[styles.tag, a.tag.className].join(" ")}>{a.tag.label}</div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              <section className={styles.panelCard}>
                <h3 className={styles.panelTitle}>Recent Activities</h3>
                <p className={styles.panelSub}>Latest events and updates from your society</p>

                <div className={styles.activityList}>
                  {auditLogsError ? (
                    <div className={styles.activityTime}>{auditLogsError}</div>
                  ) : auditActivityRows.length === 0 ? (
                    <div className={styles.activityTime}>No activity logs yet.</div>
                  ) : (
                  auditActivityRows.slice(0, 5).map((a) => (
                    <div key={a.key} className={styles.activityRow}>
                      <div className={styles.avatarStub} aria-hidden="true" />
                      <div className={styles.activityBody}>
                        <div className={styles.activityName}>{a.name}</div>
                        <div className={styles.activityText}>{a.text}</div>
                        <div className={styles.activityTime}>{a.time}</div>
                      </div>
                      <div className={[styles.tag, a.tag.className].join(" ")}>{a.tag.label}</div>
                    </div>
                  )))}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default Dashboard;



